<template>
  <section
    v-if="hasAnyData"
    class="replay-section"
    aria-label="Replay stats for the selected range"
  >
    <div class="replay-grid" :class="`replay-grid--n${cardCount}`">
      <article
        v-if="hasAchievements"
        class="top-card achievements-card"
        aria-label="Recently unlocked achievements"
      >
        <div class="card-icon" aria-hidden="true">🏆</div>
        <div class="card-label">{{ $t('replay.lastUnlocked') }}</div>
        <div class="card-value ach-count">
          <span class="ach-count-num">{{ achievementCountLabel }}</span>
          <span class="ach-count-unit">{{ $t('replay.unlocked') }}</span>
        </div>
        <ul class="ach-icons">
          <li
            v-for="(ach, i) in achievements.items"
            :key="`${ach.appId}-${i}`"
            class="ach-icon"
            tabindex="0"
            @mouseenter="showAchievementTip(ach, $event)"
            @mouseleave="activeAchievement = null"
            @focus="showAchievementTip(ach, $event)"
            @blur="activeAchievement = null"
          >
            <img
              :src="achievementIconUrl(ach.icon)"
              :alt="ach.name || 'Achievement'"
              class="ach-icon-img"
              loading="lazy"
              @error="onImageError"
            />
          </li>
        </ul>

        <!-- Anchored to the card, not to the icon. An icon-centred tooltip
             overflowed the card on the leftmost icons and got cut off by
             .achievements-card's overflow-x: clip (which has to stay — it is
             what stops page-level horizontal overflow on narrow viewports).
             Positioning against the card lets us clamp inside it instead. -->
        <div
          v-if="activeAchievement"
          ref="achTooltip"
          class="ach-tooltip"
          :style="achTooltipStyle"
          role="status"
          aria-live="polite"
        >
          <strong v-if="activeAchievement.name">{{ activeAchievement.name }}</strong>
          <span v-if="activeAchievement.gameName" class="ach-icon-game">{{ activeAchievement.gameName }}</span>
        </div>
      </article>

      <article v-if="streak.days > 0" class="top-card streak-card" aria-label="Longest streak">
        <div class="card-icon" aria-hidden="true">🔥</div>
        <div class="card-label">{{ $t('replay.longestStreak') }}</div>
        <div class="card-value streak-value">
          <span class="streak-days">{{ streak.days }}</span>
          <span class="streak-days-unit">{{ $tc('replay.day', streak.days) }}</span>
        </div>
        <div class="card-sub streak-range">
          {{ formatDateKey(streak.startDate) }} <span aria-hidden="true">→</span> {{ formatDateKey(streak.endDate) }}
        </div>
        <div class="card-sub">{{ $t('replay.inThisStreak', { playtime: formatPlaytime(streak.totalMinutes) }) }}</div>

        <ul v-if="streak.games.length" class="streak-games">
          <li
            v-for="game in streak.games"
            :key="game.appId"
            class="streak-game"
          >
            <img
              :src="getGameImageUrl(game.appId)"
              :alt="game.name || `App ${game.appId}`"
              class="streak-game-thumb"
              loading="lazy"
              @error="onImageError"
            />
            <div class="streak-game-meta">
              <span class="streak-game-name">{{ game.name || `App ${game.appId}` }}</span>
              <span class="streak-game-minutes">{{ formatPlaytime(game.minutes) }}</span>
            </div>
          </li>
        </ul>
      </article>

      <article v-if="monthlyChartData.length >= 2" class="top-card monthly-card" aria-label="Playtime by month">
        <div class="card-icon" aria-hidden="true">📅</div>
        <div class="card-label">{{ $t('replay.playtimeByMonth') }}</div>
        <div
          class="monthly-chart"
          :class="{ 'monthly-chart--sparse': monthlyChartData.length <= 4 }"
          role="img"
          :aria-label="`Playtime per month, ${monthlyChartData.length} months`"
        >
          <div
            v-for="month in monthlyChartData"
            :key="month.month"
            class="monthly-column"
          >
            <div class="monthly-bar-wrap">
              <div
                class="monthly-bar"
                :style="{ height: month.barHeightPct + '%' }"
                :title="`${month.label}: ${formatPlaytime(month.totalMinutes)}`"
              ></div>
            </div>
            <span class="monthly-month-label">{{ month.shortLabel }}</span>
            <span class="monthly-month-value">{{ formatHoursShort(month.totalMinutes) }}</span>
          </div>
        </div>
      </article>

      <article
        v-if="typeBreakdown.length"
        class="top-card types-card"
        aria-label="Most played types"
        @mousemove="onTooltipMouseMove"
        @mouseleave="hoveredType = null"
      >
        <div class="card-icon" aria-hidden="true">🎮</div>
        <div class="card-label">{{ $t('replay.mostPlayedTypes') }}</div>
        <div class="types-pie-wrap">
          <svg
            :viewBox="`0 0 ${pieSize} ${pieSize}`"
            class="types-pie"
            role="img"
            :aria-label="`Top types: ${typeBreakdown.map(t => t.name).join(', ')}`"
          >
            <g :transform="`translate(${pieSize / 2}, ${pieSize / 2})`">
              <path
                v-for="slice in pieSlices"
                :key="slice.name"
                :d="slice.path"
                :fill="slice.color"
                :stroke="hoveredType === slice.name ? '#fff' : 'rgba(0,0,0,0.25)'"
                stroke-width="1"
                :class="['pie-slice', { 'pie-slice--active': hoveredType === slice.name }]"
                @mouseenter="hoveredType = slice.name"
                @focus="hoveredType = slice.name"
                @click="toggleHover(slice.name)"
                tabindex="0"
              />
              <circle :r="pieInnerR" fill="var(--panel)" />
              <text
                text-anchor="middle"
                dominant-baseline="middle"
                class="pie-center-text"
                :y="-4"
              >{{ activeSlice ? activeSlice.percent + '%' : $t('replay.pieCenterTop') }}</text>
              <text
                text-anchor="middle"
                dominant-baseline="middle"
                class="pie-center-sub"
                :y="14"
              >{{ activeSlice ? activeSlice.name : $t('replay.pieCenterTypes') }}</text>
            </g>
          </svg>

          <ul class="types-legend">
            <li
              v-for="slice in pieSlices"
              :key="slice.name"
              :class="['types-legend-item', { 'types-legend-item--active': hoveredType === slice.name }]"
              @mouseenter="hoveredType = slice.name"
              @click="toggleHover(slice.name)"
            >
              <span class="types-legend-swatch" :style="{ background: slice.color }"></span>
              <span class="types-legend-name">{{ slice.name }}</span>
              <span class="types-legend-minutes">{{ formatPlaytime(slice.minutes) }}</span>
            </li>
          </ul>
        </div>

        <!-- Floating tooltip — anchored to mouse position, not a row below.
             Card height stays stable as the user moves between slices. -->
        <div
          v-if="activeSlice && activeSlice.games.length"
          class="types-tooltip-floating"
          :style="tooltipPositionStyle"
          role="status"
          aria-live="polite"
        >
          <div class="types-tooltip-header">
            <span class="types-tooltip-name">{{ activeSlice.name }}</span>
            <span class="types-tooltip-meta">{{ formatPlaytime(activeSlice.minutes) }} · {{ activeSlice.percent }}%</span>
          </div>
          <ul class="types-tooltip-games">
            <li v-for="g in activeSlice.games" :key="g.appId">
              <span class="types-tooltip-game-name">{{ g.name || `App ${g.appId}` }}</span>
              <span class="types-tooltip-game-minutes">{{ formatPlaytime(g.minutes) }}</span>
            </li>
          </ul>
        </div>
      </article>
      <ActivityCalendar
        class="calendar-span"
        :playtime-data="playtimeData"
        :range-start="rangeStart"
        :range-end="rangeEnd"
      />
    </div>
  </section>
</template>

<script>
import ActivityCalendar from './ActivityCalendar.vue'
import { achievementIconUrl } from '../../utils/achievements'

const MONTH_SHORT_LOCALE_OPTS = { month: 'short' };
// Generated by sampling Steam-blue + complementary hues so slices are visually
// distinct against the Steam dark theme. Top-5 + a "Other" bucket = 6 colors.
const PIE_PALETTE = ['#66c0f4', '#ae75ff', '#5bd1a4', '#ffb454', '#f08c8c', '#5c7a99']

export default {
  name: 'DashboardReplaySection',
  components: {
    ActivityCalendar
  },
  props: {
    // Active filter bounds (ISO keys) for the calendar's grid shape.
    rangeStart: { type: String, default: '' },
    rangeEnd: { type: String, default: '' },
    // Filter-scoped per-(date, app_id, minutes) records — same shape as
    // Dashboard.vue's `playtimeData` after filtering. Streak / monthly
    // breakdown / type aggregation are derived from this so they all
    // respect the active date range.
    playtimeData: { type: Array, default: () => [] },
    appNameById: { type: Object, default: () => ({}) },
    // Injected from Dashboard so image-URL resolution (incl. DB overrides) has one source of truth.
    getGameImageUrl: { type: Function, required: true },
    // Static per-app types map { appId: [{ name, source }] }, fetched once
    // from /api/users/:steamId/stats.
    appTypes: { type: Object, default: () => ({}) },
    // Date-range-scoped "Last Unlocked" card payload from the diff response:
    // { unlockedCount, capped, items:[{appId,name,icon,date}] }. Already
    // filtered + capped server-side, so this component just renders it.
    achievements: { type: Object, default: () => ({ unlockedCount: 0, capped: false, items: [] }) }
  },
  data() {
    return {
      hoveredType: null,
      // Tooltip anchor — relative to the card. mousemove writes here so the
      // floating popover follows the cursor.
      tooltipX: 0,
      tooltipY: 0,
      pieSize: 200,
      pieOuterR: 92,
      pieInnerR: 56,
      // Estimated tooltip dimensions for clamping near edges. Re-measured
      // after first render via the ref, but reasonable defaults keep us out
      // of the gutters before measurement lands.
      tooltipWidth: 220,
      tooltipHeight: 160,
      activeAchievement: null,
      achTooltipLeft: 0,
      achTooltipTop: 0
    }
  },
  computed: {
    dailyMinutes() {
      // Aggregate across apps per date, sorted ascending. Used for streak math.
      const perDay = new Map()
      for (const r of this.playtimeData) {
        const date = typeof r.date === 'string' ? r.date.slice(0, 10) : ''
        if (!date) continue
        const minutes = Number(r.playtime_forever) || 0
        if (minutes <= 0) continue
        perDay.set(date, (perDay.get(date) || 0) + minutes)
      }
      return [...perDay.entries()]
        .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
        .map(([date, minutes]) => ({ date, minutes }))
    },
    streak() {
      // Walk dailyMinutes for the longest consecutive-day run. Tie → newest.
      const days = this.dailyMinutes
      if (!days.length) return { days: 0, startDate: '', endDate: '', totalMinutes: 0, games: [] }
      let bestDays = 0, bestStart = '', bestEnd = '', bestMinutes = 0
      let runStart = '', runMinutes = 0, runDays = 0, prev = ''
      for (const row of days) {
        const continues = prev && this.daysBetween(prev, row.date) === 1
        if (continues) {
          runDays += 1
          runMinutes += row.minutes
        } else {
          runDays = 1
          runStart = row.date
          runMinutes = row.minutes
        }
        if (runDays >= bestDays) {
          bestDays = runDays
          bestStart = runStart
          bestEnd = row.date
          bestMinutes = runMinutes
        }
        prev = row.date
      }
      // Sum minutes per app inside the streak window, take top 5
      const perGame = new Map()
      for (const r of this.playtimeData) {
        const d = typeof r.date === 'string' ? r.date.slice(0, 10) : ''
        if (!d || d < bestStart || d > bestEnd) continue
        const m = Number(r.playtime_forever) || 0
        if (m <= 0) continue
        perGame.set(r.app_id, (perGame.get(r.app_id) || 0) + m)
      }
      const games = [...perGame.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([appId, minutes]) => ({
          appId,
          minutes,
          name: this.appNameById?.[appId] || this.appNameById?.[String(appId)] || ''
        }))
      return { days: bestDays, startDate: bestStart, endDate: bestEnd, totalMinutes: bestMinutes, games }
    },
    monthlyChartData() {
      const perMonth = new Map()
      for (const r of this.playtimeData) {
        const d = typeof r.date === 'string' ? r.date.slice(0, 10) : ''
        if (!d) continue
        const minutes = Number(r.playtime_forever) || 0
        if (minutes <= 0) continue
        const month = d.slice(0, 7)
        perMonth.set(month, (perMonth.get(month) || 0) + minutes)
      }
      const items = [...perMonth.entries()]
        .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0))
      if (!items.length) return []
      const max = items.reduce((m, [, mins]) => Math.max(m, mins), 0)
      return items.map(([month, mins]) => ({
        month,
        totalMinutes: mins,
        barHeightPct: max > 0 ? Math.max((mins / max) * 100, mins > 0 ? 4 : 0) : 0,
        label: this.formatMonthKey(month, 'long'),
        shortLabel: this.formatMonthKey(month, 'short')
      }))
    },
    typeBreakdown() {
      // Sum per-(type, app) minutes within the filter window via appTypes map.
      // De-dupe across sources by lowercased name (tag wins ties).
      if (!this.appTypes || typeof this.appTypes !== 'object') return []
      const perType = new Map()
      for (const r of this.playtimeData) {
        const minutes = Number(r.playtime_forever) || 0
        if (minutes <= 0) continue
        const types = this.appTypes[r.app_id] || this.appTypes[String(r.app_id)]
        if (!Array.isArray(types) || !types.length) continue
        // Rows are per (app, day), so the de-dupe is per row: a name listed as
        // both tag and genre (Action) counts once, but every day counts.
        const seen = new Set()
        for (const t of types) {
          const key = String(t.name || '').toLowerCase().trim()
          if (!key) continue
          if (seen.has(key)) continue
          seen.add(key)
          if (!perType.has(key)) {
            perType.set(key, { name: t.name, source: t.source, minutes: 0, games: new Map() })
          }
          const slot = perType.get(key)
          slot.minutes += minutes
          slot.games.set(r.app_id, (slot.games.get(r.app_id) || 0) + minutes)
          // Prefer 'tag' over 'genre' when reporting source for the slot
          if (t.source === 'tag') slot.source = 'tag'
        }
      }
      const ordered = [...perType.values()]
        .sort((a, b) => b.minutes - a.minutes || a.name.localeCompare(b.name))
        .slice(0, 5)
      return ordered.map(slot => {
        const games = [...slot.games.entries()]
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([appId, minutes]) => ({
            appId,
            minutes,
            name: this.appNameById?.[appId] || this.appNameById?.[String(appId)] || ''
          }))
        return { name: slot.name, source: slot.source, minutes: slot.minutes, games }
      })
    },
    pieSlices() {
      const total = this.typeBreakdown.reduce((s, t) => s + t.minutes, 0)
      if (total <= 0) return []
      let cumulative = 0
      return this.typeBreakdown.map((t, i) => {
        const fraction = t.minutes / total
        const start = cumulative
        cumulative += fraction
        const end = cumulative
        return {
          name: t.name,
          minutes: t.minutes,
          percent: Math.round(fraction * 100),
          color: PIE_PALETTE[i % PIE_PALETTE.length],
          path: this.donutPath(start, end, this.pieOuterR, this.pieInnerR),
          games: t.games
        }
      })
    },
    activeSlice() {
      if (!this.hoveredType) return null
      return this.pieSlices.find(s => s.name === this.hoveredType) || null
    },
    tooltipPositionStyle() {
      // Position the floating tooltip 14px to the right of and 14px above
      // the cursor by default. If that would clip the right edge of the
      // card, mirror to the left.
      const offsetX = 14
      const offsetY = -14
      // Card width is set by the grid; use the parent ref measurement.
      const cardW = this._lastCardWidth || 320
      const cardH = this._lastCardHeight || 320
      const tw = this.tooltipWidth
      const th = this.tooltipHeight
      let left = this.tooltipX + offsetX
      let top = this.tooltipY + offsetY
      if (left + tw > cardW - 8) left = Math.max(8, this.tooltipX - tw - offsetX)
      if (top < 8) top = 8
      if (top + th > cardH - 8) top = Math.max(8, cardH - th - 8)
      return {
        left: `${left}px`,
        top: `${top}px`
      }
    },
    achTooltipStyle() {
      return {
        left: `${this.achTooltipLeft}px`,
        top: `${this.achTooltipTop}px`
      }
    },
    hasAchievements() {
      return Array.isArray(this.achievements?.items) && this.achievements.items.length > 0
    },
    achievementCountLabel() {
      return `${Number(this.achievements?.unlockedCount) || 0}`
    },
    // How many of the four cards above render — the grid picks its columns from
    // this so a row is never left with one card and a hole beside it.
    // Must match each card's v-if.
    cardCount() {
      return [
        this.hasAchievements,
        this.streak.days > 0,
        this.monthlyChartData.length >= 2,
        this.typeBreakdown.length > 0
      ].filter(Boolean).length
    },
    hasAnyData() {
      // Match the v-if thresholds on the individual cards. monthly card only
      // renders with >=2 buckets; if it's the only data we'd render an empty
      // section.
      if (this.hasAchievements) return true
      if (this.streak.days > 0) return true
      if (this.monthlyChartData.length >= 2) return true
      if (this.typeBreakdown.length > 0) return true
      return false
    }
  },
  methods: {
    daysBetween(a, b) {
      const aT = new Date(`${a}T00:00:00Z`).getTime()
      const bT = new Date(`${b}T00:00:00Z`).getTime()
      return Math.round((bT - aT) / 86400000)
    },
    donutPath(start, end, rOuter, rInner) {
      // SVG donut slice path. start/end are 0..1 around the full circle,
      // 0 starts at 12 o'clock and goes clockwise.
      // Edge case: a single 100% slice can't be drawn as one arc — draw two
      // halves so it forms a full ring.
      if (end - start >= 1) {
        return [
          this.donutPath(0, 0.5, rOuter, rInner),
          this.donutPath(0.5, 1, rOuter, rInner)
        ].join(' ')
      }
      const a0 = (start * 2 * Math.PI) - Math.PI / 2
      const a1 = (end * 2 * Math.PI) - Math.PI / 2
      const x0o = Math.cos(a0) * rOuter, y0o = Math.sin(a0) * rOuter
      const x1o = Math.cos(a1) * rOuter, y1o = Math.sin(a1) * rOuter
      const x0i = Math.cos(a0) * rInner, y0i = Math.sin(a0) * rInner
      const x1i = Math.cos(a1) * rInner, y1i = Math.sin(a1) * rInner
      const largeArc = end - start > 0.5 ? 1 : 0
      return [
        `M ${x0o} ${y0o}`,
        `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x1o} ${y1o}`,
        `L ${x1i} ${y1i}`,
        `A ${rInner} ${rInner} 0 ${largeArc} 0 ${x0i} ${y0i}`,
        'Z'
      ].join(' ')
    },
    toggleHover(name) {
      this.hoveredType = this.hoveredType === name ? null : name
    },
    onTooltipMouseMove(e) {
      // Track cursor position relative to the card so the floating tooltip
      // follows the mouse. Stash the card dimensions in the same handler so
      // tooltipPositionStyle has fresh bounds for edge-clamping.
      const card = e.currentTarget
      if (!card) return
      const rect = card.getBoundingClientRect()
      this._lastCardWidth = rect.width
      this._lastCardHeight = rect.height
      this.tooltipX = e.clientX - rect.left
      this.tooltipY = e.clientY - rect.top
    },
    showAchievementTip(ach, e) {
      if (!ach || (!ach.name && !ach.gameName)) return
      const icon = e?.currentTarget
      const card = icon?.closest?.('.achievements-card')
      if (!icon || !card) return

      const cardRect = card.getBoundingClientRect()
      const iconRect = icon.getBoundingClientRect()
      this._achAnchor = {
        centre: iconRect.left - cardRect.left + iconRect.width / 2,
        top: iconRect.top - cardRect.top,
        cardWidth: cardRect.width
      }
      this.activeAchievement = ach

      // Place it once the real width is known — the tooltip is width:max-content
      // so it cannot be measured before it renders, and guessing would either
      // clip on the left or drift off-centre.
      this.$nextTick(() => this.positionAchievementTip())
    },
    positionAchievementTip() {
      const anchor = this._achAnchor
      const el = this.$refs.achTooltip
      if (!anchor || !el) return

      const width = el.offsetWidth
      const height = el.offsetHeight
      const margin = 8
      const maxLeft = Math.max(margin, anchor.cardWidth - width - margin)
      this.achTooltipLeft = Math.min(Math.max(anchor.centre - width / 2, margin), maxLeft)
      this.achTooltipTop = anchor.top - height - 6
    },
    onImageError(e) {
      const el = e?.target
      if (el) el.style.visibility = 'hidden'
    },
    achievementIconUrl,
    formatPlaytime(minutes) {
      const m = Number(minutes) || 0
      if (m <= 0) return this.$t('common.zeroMinutes')
      if (m < 60) return this.$t('common.minutesOnly', { m })
      const hours = Math.floor(m / 60)
      const remainingMinutes = m % 60
      return remainingMinutes > 0
        ? this.$t('common.hoursMinutes', { h: hours, m: remainingMinutes })
        : this.$t('common.hoursOnly', { h: hours })
    },
    formatHoursShort(minutes) {
      const m = Number(minutes) || 0
      if (m <= 0) return this.$t('common.zeroHours')
      const hours = m / 60
      const rounded = hours >= 10 ? Math.round(hours) : Math.round(hours * 10) / 10
      return this.$t('common.hoursOnly', { h: rounded })
    },
    formatDateKey(value) {
      if (!value || typeof value !== 'string') return ''
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
      if (!m) return value
      const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
      try {
        return new Intl.DateTimeFormat(this.$i18n.locale === 'tr' ? 'tr-TR' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(date)
      } catch (_) {
        return value
      }
    },
    formatMonthKey(value, variant = 'short') {
      if (!value || typeof value !== 'string') return ''
      const m = /^(\d{4})-(\d{2})/.exec(value)
      if (!m) return value
      const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1))
      try {
        const opts = variant === 'long'
          ? { month: 'long', year: 'numeric', timeZone: 'UTC' }
          : { ...MONTH_SHORT_LOCALE_OPTS, timeZone: 'UTC' }
        return new Intl.DateTimeFormat(this.$i18n.locale === 'tr' ? 'tr-TR' : 'en-US', opts).format(date)
      } catch (_) {
        return value
      }
    }
  }
}
</script>

<style scoped>
.replay-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.replay-grid {
  display: grid;
  grid-template-columns: repeat(var(--replay-cols, 1), minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
}

/* Columns follow the card count, not an auto-fit minimum width: auto-fit put
   three of four cards on a row at ~1000-1200px and left the fourth alone
   beside an empty two-thirds of a row. Four cards are always 2+2 (never 3+1,
   and never 4 across: at ~330px the monthly chart's 13 labels run together
   and the tall types card leaves the others half empty). */
.replay-grid--n2 { --replay-cols: 2; }
.replay-grid--n3 { --replay-cols: 3; }
.replay-grid--n4 { --replay-cols: 2; }

@media (max-width: 899px) {
  .replay-grid--n3 { --replay-cols: 2; }

  /* Odd one out in a 2-up grid spans the row instead of leaving a gap. */
  .replay-grid--n3 > :nth-child(3) {
    grid-column: 1 / -1;
  }
}

/* GitHub-style calendar spans the full row; min-width: 0 lets its inner
   horizontal scroller work instead of stretching the grid on phones. */
.calendar-span {
  grid-column: 1 / -1;
  min-width: 0;
}

/* Card chrome — matches SummaryMetricCard.vue exactly */
.top-card {
  background: var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.2s ease;
  min-width: 0;
}

.top-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--primary), rgba(102, 192, 244, 0.25));
  border-radius: 8px 8px 0 0;
}

.top-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
  border-color: rgba(42, 71, 94, 0.9);
}

/* Clip horizontally so an edge icon's hover tooltip (absolutely positioned,
   laid out even while hidden) can't add page-level horizontal overflow on
   narrow viewports. overflow-x: clip keeps overflow-y visible, so the tooltip
   still shows above the icon. */
.achievements-card {
  overflow-x: clip;
}

.ach-count {
  display: flex;
  align-items: baseline;
  gap: 7px;
}

.ach-count-num {
  font-size: 1.9rem;
  font-weight: 700;
  color: var(--text);
  line-height: 1;
}

.ach-count-unit {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--muted);
}

.ach-icons {
  list-style: none;
  margin: 14px 0 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ach-icon {
  position: relative;
}

.ach-icon-img {
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--panel-border);
  display: block;
  background: rgba(255, 255, 255, 0.03);
  transition: transform 0.12s ease, border-color 0.12s ease;
}

.ach-icon:hover .ach-icon-img,
.ach-icon:focus-visible .ach-icon-img {
  transform: translateY(-2px);
  border-color: var(--primary);
}

.ach-icon:focus {
  outline: none;
}

.ach-icon:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
  border-radius: 8px;
}

/* Positioned in JS against the card box (see showAchievementTip) so it can be
   clamped inside the card. width: max-content lets it size to the text; the
   clamp uses the measured width, so it is never cut off at either edge. */
.ach-tooltip {
  position: absolute;
  width: max-content;
  max-width: min(240px, calc(100% - 16px));
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: var(--panel);
  border: 1px solid var(--panel-border);
  color: var(--text);
  font-size: 0.72rem;
  padding: 5px 9px;
  border-radius: 6px;
  pointer-events: none;
  z-index: 5;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.32);
}

.ach-tooltip strong {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ach-icon-game {
  color: var(--muted);
  font-size: 0.66rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ach-icon:hover .ach-icon-name {
  opacity: 1;
}

.card-icon {
  font-size: 1.25rem;
  line-height: 1;
  margin-bottom: 8px;
  color: var(--primary);
}

.card-label {
  color: var(--muted);
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  margin-bottom: 6px;
}

.card-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--text);
}

.card-sub {
  font-size: 0.85rem;
  color: var(--muted);
  margin-top: 4px;
}

/* Streak card */
.streak-value {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.streak-days {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--text);
}

.streak-days-unit {
  color: var(--muted);
  font-size: 0.95rem;
  font-weight: 500;
  text-transform: lowercase;
}

.streak-range {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
}

.streak-games {
  list-style: none;
  margin: 12px 0 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.streak-game {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 6px;
  background: rgba(8, 12, 20, 0.5);
  border: 1px solid rgba(42, 71, 94, 0.4);
}

.streak-game-thumb {
  width: 70px;
  height: 33px;
  object-fit: cover;
  border-radius: 4px;
}

.streak-game-meta {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.streak-game-name {
  color: var(--text);
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.streak-game-minutes {
  color: var(--muted);
  font-size: 0.8rem;
}

/* Monthly card */
.monthly-chart {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  /* Fill whatever height the grid row gives the card (neighbours are tall) —
     a fixed 160px left a dead band under the labels at sparse ranges. */
  flex: 1;
  min-height: 160px;
  margin-top: 4px;
  padding: 8px 0;
}

.monthly-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  height: 100%;
}

/* With only a couple of months, flex:1 columns spread two 24px-capped bars
   across the whole card and the chart reads as broken rather than sparse.
   Below 5 months, stop stretching: fix the column width, widen the bar and
   centre the group so it looks deliberate. */
.monthly-chart--sparse {
  justify-content: center;
  gap: 16px;
}

/* Shrinkable (flex-basis, not fixed width): four 68px columns + 28px gaps
   overflowed the card's padding and the edge bars sat flush on the border. */
.monthly-chart--sparse .monthly-column {
  flex: 0 1 56px;
  min-width: 0;
}

.monthly-chart--sparse .monthly-bar {
  width: 100%;
  max-width: 40px;
}

.monthly-bar-wrap {
  width: 100%;
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.monthly-bar {
  width: 70%;
  max-width: 24px;
  /* Bottom stays clearly visible — at 0.25 tall bars faded out near the
     baseline and looked like they were hanging from the top of the card. */
  background: linear-gradient(180deg, var(--primary) 0%, rgba(102, 192, 244, 0.55) 100%);
  border-radius: 4px 4px 0 0;
  min-height: 2px;
  transition: height 0.4s ease;
}

.monthly-month-label {
  font-size: 0.72rem;
  color: var(--muted);
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.monthly-month-value {
  font-size: 0.7rem;
  color: var(--text);
}

/* Types card — donut + legend */
.types-pie-wrap {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.types-pie {
  width: 200px;
  height: 200px;
  flex-shrink: 0;
}

.pie-slice {
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.15s ease;
  transform-origin: center;
  transform-box: fill-box;
}

.pie-slice:not(.pie-slice--active):hover {
  opacity: 0.92;
}

.pie-slice--active {
  filter: brightness(1.18);
}

.pie-center-text {
  fill: var(--text);
  font-size: 18px;
  font-weight: 700;
}

.pie-center-sub {
  fill: var(--muted);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.types-legend {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 140px;
}

.types-legend-item {
  display: grid;
  grid-template-columns: 12px 1fr auto;
  gap: 8px;
  align-items: center;
  padding: 4px 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.types-legend-item:hover,
.types-legend-item--active {
  background: rgba(102, 192, 244, 0.1);
}

.types-legend-swatch {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  flex-shrink: 0;
}

.types-legend-name {
  color: var(--text);
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.types-legend-minutes {
  color: var(--muted);
  font-size: 0.78rem;
}

.types-tooltip-floating {
  position: absolute;
  z-index: 30;
  pointer-events: none;
  min-width: 180px;
  max-width: 240px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(8, 12, 20, 0.95);
  border: 1px solid rgba(102, 192, 244, 0.4);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  /* Subtle fade so the tooltip doesn't pop in jarringly. */
  animation: tooltip-fade-in 0.12s ease-out;
}

@keyframes tooltip-fade-in {
  from { opacity: 0; transform: translateY(-2px); }
  to { opacity: 1; transform: translateY(0); }
}

/* The card needs to be a positioning context for the floating tooltip. */
.types-card {
  position: relative;
}

.types-tooltip-header {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  align-items: baseline;
  margin-bottom: 6px;
}

.types-tooltip-name {
  color: var(--text);
  font-weight: 700;
  font-size: 0.95rem;
}

.types-tooltip-meta {
  color: var(--muted);
  font-size: 0.8rem;
}

.types-tooltip-games {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.types-tooltip-games li {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.85rem;
}

.types-tooltip-game-name {
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.types-tooltip-game-minutes {
  color: var(--muted);
  flex-shrink: 0;
}

@media (max-width: 640px) {
  .replay-grid {
    --replay-cols: 1;
  }
  /* A year is 13 columns in ~310px: at the desktop 6px gap and 0.72rem the
     labels ran into each other ("SEPOCTNOV"). */
  .monthly-chart:not(.monthly-chart--sparse) {
    gap: 2px;
  }
  .monthly-month-label {
    font-size: 0.6rem;
    letter-spacing: 0;
  }
  .monthly-month-value {
    font-size: 0.6rem;
  }
  .types-pie-wrap {
    justify-content: center;
  }
  .types-pie {
    width: 180px;
    height: 180px;
  }
}
</style>
